import { GRANDEZAS, type ColunaDoMotor, type Grandeza, type Premissas } from '../../cptu/motor';
import { numeroBr, type DefinicaoDaTrilha } from '../../cptu/visual';

/**
 * «Parâmetros básicos» | «Parâmetros correlacionados» na tela da vertical — onda 315, Tarefa 315.5b (os pedidos do
 * Humberto de 12/10/2026, itens 2, 4, 5 e 6, vendo o CPeT-IT 3.9.7.28).
 *
 * Os básicos são as trilhas de hoje (qt, fs, u₂, Rf, Ic e o «＋ Trilha») e a tabela Medidos · Corrigidos e
 * normalizados · Classificação. Os correlacionados são os grupos das abas «Estimations Plots» do CPeT-IT, nessa
 * ordem (o engenheiro já os conhece), e a «Strength Loss» como «Perda de resistência»; a tabela leva a profundidade,
 * o qc, o fs e o SBTn fixos à esquerda e as correlações dos grupos.
 *
 * Só o que o motor calcula: o IP e o Su amolgado em kPa não existem nele (o Su(liq)/σ'v é a razão `suR`) — ficam de
 * fora, com a nota, e não se inventam (a spec 08 com gabarito). O que não é homologado vai com a marca «▲» da 313
 * (as `Trilhas` e a tabela a põem). A faixa contrátil × dilatante do Qtn,cs não entra (sem fonte assinada): só a linha
 * do limite de Qtn,cs das premissas, rotulada como o motor a chama e com «sem fonte».
 */

export type Parametros = 'basicos' | 'correlacionados';

/** Um grupo da vista dos correlacionados: a chave do endereço, o título e as trilhas. */
export interface GrupoCorrelacionado {
  chave: string;
  titulo: string;
  grandezas: readonly Grandeza[];
  /** O que o grupo do CPeT-IT tem e o motor não calcula, escrito embaixo das trilhas. */
  nota: string | null;
}

/** Os grupos, na ordem das abas do CPeT-IT. */
export const GRUPOS_CORRELACIONADOS: readonly GrupoCorrelacionado[] = Object.freeze([
  { chave: '1', titulo: "1 · k, N60, Es, Dr, φ'", grandezas: ['k', 'n60', 'es', 'dr', 'phi'], nota: null },
  { chave: '2', titulo: "2 · M, G₀, Su, Su/σ'v, OCR", grandezas: ['m', 'g0', 'su', 'suRazao', 'ocr'], nota: null },
  { chave: '3', titulo: "3 · Vs, ψ, K₀, St, φ' de pico", grandezas: ['vs', 'psi', 'k0', 'st', 'phiPico'], nota: null },
  {
    chave: '4', titulo: '4 · γ, w, e, γd', grandezas: ['gama', 'w', 'e', 'gamaD'],
    nota: 'O IP do grupo do CPeT-IT não é calculado pelo motor e fica de fora. O w, o e e o γd só existem abaixo do NA '
      + '(material saturado).',
  },
  {
    chave: 'perda', titulo: 'Perda de resistência', grandezas: ['qt', 'qtn', 'ic', 'qtncs', 'suR'],
    nota: "O Su amolgado em kPa (a segunda série do CPeT-IT) não é calculado pelo motor: a trilha é a razão Su(liq)/σ'v.",
  },
] as GrupoCorrelacionado[]);

/** O grupo do endereço (`?grupo=`), ou o primeiro. */
export function grupoDoEndereco(chave: string | null): GrupoCorrelacionado {
  return GRUPOS_CORRELACIONADOS.find((g) => g.chave === chave) ?? GRUPOS_CORRELACIONADOS[0];
}

/** A vista dos parâmetros pelo endereço (`?parametros=correlacionados`): sem ela, os básicos. */
export const parametrosDoEndereco = (valor: string | null): Parametros =>
  (valor === 'correlacionados' ? 'correlacionados' : 'basicos');

/** O que vai depois do caminho da vertical: `?parametros=correlacionados&grupo=2` (os básicos não levam nada). */
export function buscaDosParametros(parametros: Parametros, grupo?: string): string {
  if (parametros === 'basicos') return '';
  return grupo && grupo !== GRUPOS_CORRELACIONADOS[0].chave ? `?parametros=correlacionados&grupo=${grupo}` : '?parametros=correlacionados';
}

/** A trilha de uma grandeza nos grupos: k em log, as demais lineares; o desenho de cada uma, como na «Perda de resistência». */
export function trilhaCorrelacionada(g: Grandeza, premissas: Premissas): DefinicaoDaTrilha {
  const d = GRANDEZAS[g];
  const base: DefinicaoDaTrilha = {
    chave: g, titulo: d.rotulo, unidade: d.unidade, escala: g === 'k' || g === 'ch' ? 'log' : 'linear',
    series: [{ coluna: g, classe: g === 'qt' ? 'serie-qt' : 'serie-extra' }],
  };
  if (g === 'qt') return { ...base, preencherPorZona: 'sbt' };
  if (g === 'qtn') return { ...base, preencherPorZona: 'sbtn' };
  if (g === 'ic') return { ...base, dominio: [1, 4], fundoDeIc: true };
  if (g === 'qtncs') {
    const limite = premissas.qtncsLimite;
    return {
      ...base,
      linha: {
        valor: limite,
        rotulo: `limite ${numeroBr(limite, 0)} · sem fonte`,
        nota: `Qtn,cs — a linha tracejada é o limite de Qtn,cs das premissas (${numeroBr(limite, 0)}), sem efeito no motor `
          + 'e sem fonte para as faixas contrátil × dilatante: a tela não as pinta.',
      },
    };
  }
  return base;
}

/** As trilhas de um grupo, com as premissas da interpretação (o limite de Qtn,cs). */
export const trilhasDoGrupo = (g: GrupoCorrelacionado, premissas: Premissas): DefinicaoDaTrilha[] =>
  g.grandezas.map((x) => trilhaCorrelacionada(x, premissas));

/**
 * As colunas da tabela dos correlacionados: as correlações dos grupos, na ordem deles, sem as que já são básicas
 * (qt, Qtn e Ic, das trilhas da «Perda de resistência»).
 */
export const COLUNAS_CORRELACIONADAS: readonly ColunaDoMotor[] = Object.freeze(GRUPOS_CORRELACIONADOS
  .flatMap((g) => g.grandezas)
  .filter((c) => c !== 'qt' && c !== 'qtn' && c !== 'ic'));
